/*
  Policy tabs.

  Everything is already in the page: the element's only job is to show one
  document at a time. Nothing is hidden until this runs, so a reader without
  JavaScript gets the whole thing stacked rather than an empty page with a row
  of dead buttons.

  The open tab is kept in the address bar, so a link to #returns opens Returns,
  and a link to a heading inside a policy opens the policy that contains it.
*/
class PolicyTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = this.tabs
      .map((tab) => this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`))
      .filter(Boolean);

    if (this.tabs.length < 2 || this.panels.length !== this.tabs.length) return;

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index, true));
      tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
    });

    this.select(this.indexFromHash(window.location.hash), false);

    window.addEventListener('hashchange', () => {
      const index = this.indexFromHash(window.location.hash);
      if (index !== this.current) this.select(index, false);
    });
  }

  /* A hash can name a policy (#returns), its panel (#doc-returns), or any
     heading inside one. */
  indexFromHash(hash) {
    if (!hash || hash.length < 2) return 0;

    const name = hash.slice(1);
    let target;
    try {
      target =
        this.querySelector(`#${CSS.escape(`doc-${name}`)}`) ||
        this.querySelector(`#${CSS.escape(name)}`);
    } catch (error) {
      return 0;
    }
    if (!target) return 0;

    const panel = target.closest('[role="tabpanel"]');
    const index = this.panels.indexOf(panel);
    return index === -1 ? 0 : index;
  }

  select(index, fromClick) {
    this.current = index;

    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
      this.panels[i].hidden = !selected;
    });

    if (!fromClick) return;

    this.tabs[index].focus();
    const anchor = this.tabs[index].getAttribute('aria-controls').replace(/^doc-/, '');
    history.replaceState(null, '', `#${anchor}`);
  }

  onKeydown(event, index) {
    const keys = { ArrowRight: 1, ArrowLeft: -1, Home: 'first', End: 'last' };
    const move = keys[event.key];
    if (move === undefined) return;

    event.preventDefault();
    let next;
    if (move === 'first') next = 0;
    else if (move === 'last') next = this.tabs.length - 1;
    else next = (index + move + this.tabs.length) % this.tabs.length;

    this.select(next, true);
  }
}

customElements.define('policy-tabs', PolicyTabs);
